// migrationCleanup.js
//
// Once a user has opted into the beta they are on the new site, so any state
// cached by the old one is stale at best. This wipes the client side caches a
// single time per user.
//
// Deliberately left alone:
//   - cookies, so the dbio_beta_opt_in cookie survives and the edge keeps
//     sending them to the new site.
//   - Firebase's own auth persistence (IndexedDB, plus its localStorage and
//     sessionStorage fallbacks), so the user stays logged in.

const MARKER_KEY = "dbio_migrationCleanup";

// Bump this to force the cleanup to run again on an already cleaned client.
const CLEANUP_VERSION = "1";

// Firebase stores auth state in IndexedDB, but falls back to Web Storage when
// that isn't available (Safari private browsing, for example). Clearing these
// would sign the user out, and arriving on the new site signed out is not what
// pressing a button on your own profile page should do.
const PRESERVED_PREFIXES = ["firebase:", "firebaseLocalStorage", MARKER_KEY];

const isPreserved = (key) =>
  PRESERVED_PREFIXES.some((prefix) => key.startsWith(prefix));

const clearStorage = (storage) => {
  if (!storage) {
    return;
  }

  const keys = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key && !isPreserved(key)) {
      keys.push(key);
    }
  }

  keys.forEach((key) => storage.removeItem(key));
};

// Neither site registers a service worker today, but an old one can still be
// controlling the page from a previous deploy, in which case it serves the old
// app regardless of what the edge routes to.
const clearServiceWorkers = async () => {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(
    registrations.map((registration) => registration.unregister())
  );
};

const clearCacheStorage = async () => {
  // caches is only defined in a secure context.
  if (typeof caches === "undefined") {
    return;
  }

  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));
};

export const runMigrationCleanup = async () => {
  try {
    if (localStorage.getItem(MARKER_KEY) === CLEANUP_VERSION) {
      return;
    }

    clearStorage(window.localStorage);
    clearStorage(window.sessionStorage);
    await clearCacheStorage();
    await clearServiceWorkers();

    // Written last so that it survives the storage wipe above, and before the
    // caller navigates, so this can't run again and loop.
    localStorage.setItem(MARKER_KEY, CLEANUP_VERSION);
  } catch (error) {
    console.error("Error clearing caches after migration:", error);
  }
};
